import objectUtils from '../../utils/object-utils'
import AnonUser from '../../models/AnonUser'
import User from '../../models/User'
import { routeLeafApi } from '../../services/routeLeafApi'
import { toModel } from '../../services/normalizer'
import {
  buildModelObject,
  parseResourceNameFromModelObject,
  syncItem,
  syncItemIntoArray
} from '../starhouse/helpers'

// Active org context header
const HEADER_NAME_ACTIVE_ORG_ID = 'X-ROUTELEAF-ACTIVE-ORG-ID'

export function generateStore (options) {
  const debug = options.debug
  const pluginName = options.pluginName
  const Authenticator = options.authenticator
  const includeUserInfo = options.includeUserInfo

  const store = { namespaced: true }

  // ---------------------------------------------------------------------------
  // Store state
  // ---------------------------------------------------------------------------
  store.state = {
    pluginName,
    isAuthenticated: false,
    provider: null,
    authToken: null,

    // ----------------------------------------------------- The active app user
    user: new AnonUser(),
    userEditing: null, // the active user being edited
    userEditing_messages: [], // the queue of messages for user edits
    userEditing_activeMessage: null, // the active user message to be handled

    // ----------------------------------------------------- The active user org
    org: null,
    orgEditing: null, // the active org being edited
    orgEditing_messages: [], // the queue of messages for org edits
    orgEditing_activeMessage: null // the active org message to be handled
  }

  // ---------------------------------------------------------------------------
  // Store getters
  // ---------------------------------------------------------------------------
  store.getters = {
    pluginName (state) {
      return state.pluginName
    },
    isAuthenticated (state) {
      return state.isAuthenticated
    },
    provider (state) {
      return state.provider
    },
    authToken (state) {
      return state.authToken
    },

    // ----------------------------------------------------- The active app user
    activeUser (state) {
      return state.user
    },
    activeUserRoles (state) {
      return state.user.roles
    },
    activeDriver (state) {
      return state.user.driver
    },
    activeUserEditing (state) {
      return state.userEditing
    },
    userEditingFeedback (state) {
      return state.userEditing_activeMessage
    },

    // ----------------------------------------------------- The active user org
    activeOrg (state) {
      return state.org
    },
    activeOrgEditing (state) {
      return state.orgEditing
    },
    orgEditingFeedback (state) {
      return state.orgEditing_activeMessage
    }
  }

  // ---------------------------------------------------------------------------
  // Store actions
  // ---------------------------------------------------------------------------
  store.actions = {
    // This action will always resolve !!!
    async validateSession (context) {
      const { state, commit, dispatch, getters } = context
      if (debug) console.log(`[${pluginName}] Validating auth session...`)

      let tokenInfo = {}
      try {
        tokenInfo = await Authenticator.validateSession()
        if (debug) console.log(`[${pluginName}] Retrieved token info:`, tokenInfo)
      } catch (error) {
        if (debug) console.log(`[${pluginName}] No active user session. Enabling anonymous session.`)
        if (state.isAuthenticated) dispatch('clearSession') // clear out session (storage is explicitly purged)
        return { is_authenticated: false, token: null, user_info: null }
      }

      // Establish authenticated session
      const token = tokenInfo.token
      commit('setProvider', tokenInfo.provider) // save provider
      commit('setAuthToken', token) // save token to store
      commit('setIsAuthenticated', true) // set authenticated state
      if (debug) console.log(`[${pluginName}] A valid user session has been established.`)

      // Check if existing user info is already loaded (or if hook not enabled)
      const activeUser = getters.activeUser
      if (activeUser.is_logged_in || !includeUserInfo) {
        // Return user info and auth token
        if (debug && includeUserInfo) console.log(`[${pluginName}] Active user info already loaded:`, activeUser)
        return { is_authenticated: true, token, user_info: activeUser }
      }

      // Otherwise, fetch user info from endpoint
      try {
        const userInfo = await dispatch('fetchUserInfo')
        // If no user info obtained, just return token
        if (!userInfo) return { is_authenticated: true, token, user_info: null }

        // Build user object from info and save to store
        if (debug) console.log(`[${pluginName}] Active user info to load:`, userInfo)
        const user = new User(userInfo)
        dispatch('setActiveUser', user)
        dispatch('setActiveOrg', user.active_org)

        // Return user info and auth token
        return { is_authenticated: true, token, user_info: userInfo }
      } catch (error) {
        // If error on user info hook, force logout
        await dispatch('logoutUser')
        return { error, is_authenticated: false, token: null, user_info: null }
      }
    },

    async loginUser (context, creds) {
      const { dispatch } = context
      if (!creds) {
        const errorMessage = `[${pluginName}] Cannot login user due to missing creds.`
        if (debug) console.log(errorMessage)
        throw new Error(errorMessage)
      }

      if (debug) console.log(`[${pluginName}] Authenticating user...`)

      try {
        const tokenInfo = await Authenticator.authenticateUser(creds)
        if (debug) console.log(`[${pluginName}] Retrieved token info:`, tokenInfo)

        // Establish authenticated session, authorize against app
        const sessionInfo = await dispatch('validateSession')
        if (!sessionInfo.is_authenticated) throw sessionInfo.error // reject on failed login
        return sessionInfo // return session info on successful login
      } catch (error) {
        if (debug) console.log(`[${pluginName}] Excountered provider error when authenticating user via local login:`, error)
        throw error
      }
    },

    async oauthUser (context, authInfo) {
      const { dispatch } = context
      if (!authInfo) {
        const errorMessage = `[${pluginName}] Cannot login oauth user due to missing authInfo.`
        if (debug) console.log(errorMessage)
        throw new Error(errorMessage)
      }

      if (debug) console.log(`[${pluginName}] Authenticating user via OAuth`, authInfo)

      try {
        // Establish authenticated session, authorize against app
        const sessionInfo = await dispatch('validateSession')
        if (!sessionInfo.is_authenticated) throw sessionInfo.error // reject on failure
        return sessionInfo // return session info on successful login
      } catch (error) {
        if (debug) console.log(`[${pluginName}] Excountered provider error when authenticating user via oauth:`, error)
        throw error
      }
    },

    async fetchUserInfo (context) {
      const { getters } = context

      // Fetch user info from provider
      const provider = getters.provider
      const token = getters.authToken
      if (debug) console.log(`[${pluginName}] Fetching user info from provider ${provider}...`)

      try {
        const userInfo = await Authenticator.fetchUserInfo({ provider, token })
        if (!userInfo.avatar_url) userInfo.avatar_url = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTenyQCcge4mKO5_BdFvT9gwC80tM1NTFv7kHCYWmUxF-D6-Bs8'
        return { ...userInfo } // return user info
      } catch (error) {
        if (debug) console.log(`[${pluginName}] Excountered provider error when fetching user info:`, error)
        throw error
      }
    },

    // TODO - await axios.get('https://accounts.google.com/logout') // NOTE: Trying hack for Google logout
    async logoutUser (context) {
      const { dispatch, getters } = context

      const provider = getters.provider

      let reply = null
      try {
        reply = await Authenticator.expireUser(provider)
      } catch (error) {
        // Log error, but pass-through to complete logout from app
        if (debug) console.log(`[${pluginName}] Encountered provider error when expiring user:`, error)
      } finally {
        // Clear authenticated session
        if (debug) console.log(`[${pluginName}] Logging out user session`, reply)
        dispatch('clearSession')
      }

      return true
    },

    clearSession (context) {
      if (debug) console.log(`[${pluginName}] Clearing session`)
      context.commit('clearSession')
      context.commit('clearActiveUser')
      context.commit('clearActiveOrg')
    },

    setActiveUser (context, user) {
      context.commit('setActiveUser', user)
    },

    clearActiveUser (context) {
      context.commit('clearActiveUser')
    },

    setActiveOrg (context, org) {
      context.commit('setActiveOrg', org)
    },

    clearActiveOrg (context) {
      context.commit('clearActiveOrg')
    },

    // ---------------------------------------------------- User editing actions

    setActiveUserToEdit (context) {
      const activeUser = context.state.user
      if (debug) console.log(`[${pluginName}] Setting active user to edit =>`, activeUser)
      return context.commit('setUserToEdit', activeUser)
    },

    async updateUser (context) {
      const { state, commit } = context
      const userEditing = state.userEditing
      if (!userEditing) return false

      const uri = 'my/user'
      const opts = { bodyData: userEditing }

      // Add auth header
      const token = state.authToken
      const activeOrgId = state.org.id
      const headers = { authorization: `Bearer ${token}` }
      if (activeOrgId) headers[HEADER_NAME_ACTIVE_ORG_ID] = activeOrgId
      Object.assign(opts, { headers })

      // Perform update
      const payload = await routeLeafApi.post(uri, opts)
      const updatedUser = toModel(payload)

      // Sync into state
      commit('syncDataItem', { resource: 'user', nested: false, data: updatedUser })

      // Return updated user
      return updatedUser
    },

    clearActiveUserEditing (context) {
      context.commit('clearUserEditing')
      return Promise.resolve(true)
    },

    // ----------------------------------------------------- Org editing actions

    setActiveOrgToEdit (context) {
      const activeOrg = context.state.org
      if (debug) console.log(`[${pluginName}] Setting active org to edit =>`, activeOrg)
      return context.commit('setOrgToEdit', activeOrg)
    },

    async updateOrg (context) {
      const { state, commit } = context
      const orgEditing = state.orgEditing
      if (!orgEditing) return false

      const uri = 'my/org'
      const opts = { bodyData: orgEditing }

      // Add auth header
      const token = state.authToken
      const activeOrgId = state.org.id
      const headers = { authorization: `Bearer ${token}` }
      if (activeOrgId) headers[HEADER_NAME_ACTIVE_ORG_ID] = activeOrgId
      Object.assign(opts, { headers })

      // Perform update
      const payload = await routeLeafApi.post(uri, opts)
      const updatedOrg = toModel(payload)

      // Sync into state
      commit('syncDataItem', { resource: 'org', nested: false, data: updatedOrg })

      // Return updated org
      return updatedOrg
    },

    clearActiveOrgEditing (context) {
      context.commit('clearOrgEditing')
      return Promise.resolve(true)
    },

    // ------------------------------------------------------ Sync data handling

    // dataOpts = <the_model_object>
    //     -or-
    // dataOpts = {
    //   resource: <resource_name>,
    //   nested: <prop_name>         --> a nested property of the registry (OPTIONAL)
    //   data: <the_model_object> | { ...fields },
    //   replace: true | false,   --> Replace the existing entry entirely (default: false = merges the data)
    // }
    syncData (context, dataOpts = {}) {
      const { commit } = context
      const item = (dataOpts.model) ? dataOpts : dataOpts.data
      const resource = dataOpts.resource || parseResourceNameFromModelObject(item)
      const nested = dataOpts.nested
      const replace = dataOpts.replace
      if (!resource || !item) return Promise.reject(false) // eslint-disable-line prefer-promise-reject-errors

      if (debug) console.log(`[${pluginName}] Syncing state with "${resource}" data:`, item)
      commit('syncDataItem', { resource, nested, data: item, replace })
    }
  } // END - actions

  // ---------------------------------------------------------------------------
  // Store mutations
  // ---------------------------------------------------------------------------
  store.mutations = {
    setIsAuthenticated (state, value) {
      state.isAuthenticated = value
    },

    setAuthToken (state, token) {
      state.authToken = token
    },

    setProvider (state, provider) {
      state.provider = provider
    },

    clearSession (state) {
      // Clear token and session state
      state.isAuthenticated = false
      state.provider = null
      state.authToken = null
    },

    // -------------------------------------------------------------- User state

    setActiveUser (state, user) {
      state.user = user
    },

    clearActiveUser (state) {
      // Anonymize user context
      state.user = new AnonUser()

      // Clear out messages
      state.userEditing_messages = []
      state.userEditing_activeMessage = null
    },

    setActiveOrg (state, org) {
      state.org = org
    },

    clearActiveOrg (state) {
      state.org = null
    },

    // ------------------------------------------------- User editing management

    setUserToEdit (state, user) {
      if (user) {
        const copy = buildModelObject('user', user) // build a copy
        state.userEditing = copy
        state.userEditing_messages = []
        state.userEditing_activeMessage = null
      }
    },

    clearUserEditing (state) {
      state.userEditing = null
      state.userEditing_messages = []
      state.userEditing_activeMessage = null
    },

    registerMessageForUserEditing (state, message) {
      state.userEditing_messages.unshift(message)
    },

    advanceMessageForUserEditing (state) {
      // Advance next available message, if ready
      if (!state.userEditing_activeMessage) state.userEditing_activeMessage = state.userEditing_messages.pop()
    },

    resolveMessageForUserEditing (state) {
      state.userEditing_activeMessage = null
    },

    // -------------------------------------------------- Org editing management

    setOrgToEdit (state, org) {
      if (org) {
        const copy = buildModelObject('org', org) // build a copy
        state.orgEditing = copy
        state.orgEditing_messages = []
        state.orgEditing_activeMessage = null
      }
    },

    clearOrgEditing (state) {
      state.orgEditing = null
      state.orgEditing_messages = []
      state.orgEditing_activeMessage = null
    },

    registerMessageForOrgEditing (state, message) {
      state.orgEditing_messages.unshift(message)
    },

    advanceMessageForOrgEditing (state) {
      // Advance next available message, if ready
      if (!state.orgEditing_activeMessage) state.orgEditing_activeMessage = state.orgEditing_messages.pop()
    },

    resolveMessageForOrgEditing (state) {
      state.orgEditing_activeMessage = null
    },

    // -------------------------------------------------------------- Sync logic

    // dataOpts = {
    //   resource: <resource_name>
    //   nested: <prop_name>         --> a nested property of the registry (OPTIONAL)
    //   data: <the_model_object>    --> the new item data
    //   replace: true | false       --> replace the existing entry entirely (default: false = merges the data)
    // }
    syncDataItem (state, dataOpts = {}) {
      const resource = dataOpts.resource
      const nested = dataOpts.nested
      const item = dataOpts.data
      const replace = dataOpts.replace

      if (resource && item) {
        const itemToSync = objectUtils.cloneDeep(item)

        let target = null
        if (resource === 'driver') {
          target = (nested)
            ? objectUtils.get(state.user.driver, nested, {})
            : state.user.driver
        } else {
          target = state[resource]
        }
        if (!target) return false

        if (debug) {
          if (nested) console.log(`Syncing data into nested path "${nested}" of "${resource}":`, target)
          else console.log(`Syncing "${resource}" item into the the state:`, target)
        }

        // Perform sync
        if (Array.isArray(target)) {
          syncItemIntoArray(itemToSync, target, replace)
        } else {
          syncItem(itemToSync, target, replace)
        }
      } // end-if (resource && item)
    }
  }

  return store
} // END - generateStore()
